import i18n from '../i18n';

export class HttpError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(opts: { status: number; message: string; code?: string | null }) {
    super(opts.message);
    this.name = 'HttpError';
    this.status = opts.status;
    this.code = opts.code ?? null;
  }
}

export class ContractError extends Error {
  readonly issues: unknown;

  constructor(message: string, issues: unknown) {
    super(message);
    this.name = 'ContractError';
    this.issues = issues;
  }
}

export interface ErrorFields {
  readonly message: string;
  readonly code: string | null;
}

const text = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null);

export const errorFields = (data: unknown): ErrorFields => {
  const body = (data ?? {}) as Record<string, unknown>;
  return {
    message:
      text(body.message) ??
      text(body.error) ??
      i18n.t('common.somethingWentWrong', 'Something went wrong. Please try again.'),
    code: text(body.code),
  };
};

export type ErrorFactory = (status: number, data: unknown) => Error;

export const userFacingMessage = (error: unknown): string | null =>
  error instanceof HttpError && error.message.trim() !== '' ? error.message : null;

export const httpErrorFrom: ErrorFactory = (status, data) => new HttpError({ status, ...errorFields(data) });

export interface ApiViolation {
  readonly code: string;
  readonly message?: string;
  readonly path?: string;
  readonly keys?: string[];
  readonly autoFixable?: boolean;
}

export interface ApiErrorOptions {
  readonly status: number;
  readonly message: string;
  readonly code?: string | null;
  readonly violations?: ApiViolation[];
  readonly keys?: string[];
  readonly expectedVersion?: number | null;
  readonly currentVersion?: number | null;
  readonly retryAfterSeconds?: number | null;
}

export class ApiError extends HttpError {
  readonly violations: ApiViolation[];
  readonly keys: string[];
  readonly expectedVersion: number | null;
  readonly currentVersion: number | null;
  readonly retryAfterSeconds: number | null;

  constructor(opts: ApiErrorOptions) {
    super(opts);
    this.name = 'ApiError';
    this.violations = opts.violations ?? [];
    this.keys = opts.keys ?? [];
    this.expectedVersion = opts.expectedVersion ?? null;
    this.currentVersion = opts.currentVersion ?? null;
    this.retryAfterSeconds = opts.retryAfterSeconds ?? null;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const optionalText = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const violationOf = (value: unknown): ApiViolation | null => {
  if (!isRecord(value) || typeof value.code !== 'string') return null;
  return {
    code: value.code,
    message: optionalText(value.message),
    path: optionalText(value.path),
    keys: Array.isArray(value.keys) ? strings(value.keys) : undefined,
    autoFixable: typeof value.autoFixable === 'boolean' ? value.autoFixable : undefined,
  };
};

const violationsOf = (value: unknown): ApiViolation[] =>
  Array.isArray(value) ? value.map(violationOf).filter((item): item is ApiViolation => item !== null) : [];

const numberOrNull = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

export const apiErrorFrom: ErrorFactory = (status, data) => {
  const body = isRecord(data) ? data : {};
  return new ApiError({
    status,
    ...errorFields(data),
    violations: violationsOf(body.violations),
    keys: strings(body.keys),
    expectedVersion: numberOrNull(body.expectedVersion),
    currentVersion: numberOrNull(body.currentVersion),
    retryAfterSeconds: numberOrNull(body.retryAfterSeconds),
  });
};
